import { useState, useCallback, useEffect } from 'react';
import type { GalleryItem } from './types';

interface UseLightboxReturn {
  lightboxOpen: boolean;
  lightboxIndex: number;
  openLightbox: (index: number) => void;
  closeLightbox: () => void;
  handlePrevImage: () => void;
  handleNextImage: () => void;
}

export function useLightbox(
  filteredItems: GalleryItem[],
  lightboxEnabled: boolean
): UseLightboxReturn {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const handlePrevImage = useCallback(() => {
    setLightboxIndex(prev => (prev > 0 ? prev - 1 : filteredItems.length - 1));
  }, [filteredItems.length]);

  const handleNextImage = useCallback(() => {
    setLightboxIndex(prev => (prev < filteredItems.length - 1 ? prev + 1 : 0));
  }, [filteredItems.length]);

  const openLightbox = useCallback(
    (index: number) => {
      if (lightboxEnabled) {
        setLightboxIndex(index);
        setLightboxOpen(true);
      }
    },
    [lightboxEnabled]
  );

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLightboxOpen(false);
      } else if (e.key === 'ArrowLeft') {
        handlePrevImage();
      } else if (e.key === 'ArrowRight') {
        handleNextImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightboxOpen, handlePrevImage, handleNextImage]);

  return {
    lightboxOpen,
    lightboxIndex,
    openLightbox,
    closeLightbox,
    handlePrevImage,
    handleNextImage,
  };
}
